import { useEffect, useState } from "react";
import api from "../../lib/api";
import { apiError, formatAmount, minorToText, toMinor } from "../../lib/finance";
import { CURRENCIES } from "../../lib/claims";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const selectClass = "h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-base md:text-sm";

// An optional cap per note de frais of a category, in one currency. Signalled to whoever approves, never blocking.
export default function CategoryCapDialog({ category, defaultCurrency = "XOF", onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!category) return;
    setAmount(category.cap || "");
    setCurrency(category.cap_currency || defaultCurrency);
  }, [category, defaultCurrency]);

  const minor = toMinor(amount, currency);
  const save = async (cap) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/finance/categories/${category.id}/cap`, {
        expected_version: category.version, cap: cap === null ? null : minorToText(minor, currency), currency: cap === null ? null : currency,
      });
      toast.success(cap === null ? "Plafond retiré" : `Plafond de ${formatAmount(data.cap, data.cap_currency)} par note`);
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!category} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="category-cap-dialog">
        <DialogHeader>
          <DialogTitle>Plafond « {category?.name} »</DialogTitle>
          <DialogDescription>Montant au-delà duquel une note de frais est signalée à la personne qui approuve. Elle peut toujours être envoyée et approuvée.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[1fr_7rem] gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="cap-amount">Plafond par note</Label>
            <Input id="cap-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="text-base md:text-sm tabular-nums" data-testid="cap-amount" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cap-currency">Devise</Label>
            <select id="cap-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className={selectClass}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <p className="text-xs text-slate-500">Comparé seulement aux notes dans cette devise : aucune conversion.</p>
        <DialogFooter className="gap-2">
          {category?.cap && <Button variant="outline" onClick={() => save(null)} disabled={busy} data-testid="cap-remove">Retirer le plafond</Button>}
          <Button onClick={() => save(minor)} disabled={busy || !minor || minor <= 0} className="bg-jamora hover:bg-jamora-hover text-white" data-testid="cap-save">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
